<template>
  <div class="list" :class="size">
    <div :class="[type, size]" class="article">
      <div :class="{ 'row': landscape, 'suggestion-post': articleType === 'related-posts' }" class="article-relative">
        <nuxt-link :to="blogPostURL(article)" :title="article.title" />
        <div
          :class="{
            'col col-8': landscape
          }"
        >
          <div class="article-image">
            <img
              :loading="notLazy ? 'eager' : 'lazy'"
              :src="imageSrc"
              :srcset="imageSrcSet"
              :sizes="`(max-width: 1024px) and (min-width: 600px) 1024px,
                (max-width: 599) 520px,
                ${thumb}px`"
              :alt="article.title"
            >
          </div>
        </div>
        <div
          :class="{ 'col col-4': landscape }"
        >
          <div class="info">
            <p class="category">
              {{ article.categoryName }}
            </p>
            <component :is="heading" :class="type === 'vertical' ? 'h6' : 'h5'" class="title">
              {{ article.title }}
            </component>
            <p v-if="size === 'lg'" class="body-1">
              {{ article.metaDescription }}
            </p>
            <div class="post-info">
              <template v-if="article.authorName">
                <nuxt-link class="author-a author-link" :to="article.authorSlug ? `/blog/authors/${article.authorSlug}/` : ''">
                  <img :loading="notLazy ? 'eager' : 'lazy'" :src="`${$config.imagesUrl}/team/thumb@128_${article.authorImage}`" :alt="`Article by ${article.authorName}`">
                  <span class="caption">by <b>{{ article.authorName }}</b></span>
                </nuxt-link>
                <span class="caption bullet" />
              </template>
              <span class="caption">{{ article.readingTime }} min read</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import global from '@itbpbg/lms-components/src/mixins/global';

export default {
  mixins: [global],
  props: {
    article: {
      type: Object,
      required: true,
    },
    type: {
      type: String,
      required: false,
      default: 'vertical',
    },
    size: {
      type: String,
      required: false,
      default: 'sm',
    },
    articleType: {
      type: String,
      required: false,
      default: '',
    },
    heading: {
      type: String,
      default: 'h3',
      required: false,
    },
    notLazy: Boolean,
  },
  computed: {
    thumb() {
      if (this.size === 'lg') {
        return '1024';
      }
      if (this.type === 'vertical' && this.size === 'sm') {
        return '520';
      }
      return '360';
    },
    imageSrc() {
      return this.$webp(`${this.$config.imagesUrl}/blog/thumb@${this.thumb}_${this.article.image}`);
    },
    imageSrcSet() {
      const thumb1024 = this.$webp(`${this.$config.imagesUrl}/blog/thumb@1024_${this.article.image}`);
      const thumb520 = this.$webp(`${this.$config.imagesUrl}/blog/thumb@520_${this.article.image}`);

      return `${this.imageSrc} ${this.thumb}w,${thumb1024} 1024w,${thumb520} 520w`;
    },
    landscape() {
      return this.type === 'horizontal' && this.size === 'lg';
    },
  },
};
</script>

<style scoped lang="scss">
.list {
  list-style: none;

  .article {
    > .article-relative {
      color: inherit;
      width: 100%;
      display: block;
      position: relative;

      > a {
        position: absolute;
        height: 100%;
        width: 100%;
        cursor: pointer;
        z-index: 1;
      }
    }

    .article-image {
      border-radius: 10px;
      overflow: hidden;
      position: relative;
      transition: all var(--duration) ease;
      width: 100%;

      img {
        position: absolute;
        height: 100%;
        width: 100%;
        object-fit: cover;
        object-position: center;
      }
    }

    .info {
      margin-top: var(--spacing-16);

      p:not(.title) {
        font-weight: 600;
        line-height: 100%;
        text-transform: capitalize;
        color: var(--accent-shade-2);
        margin: 0;
      }

      .title {
        margin: var(--spacing-12) 0;
      }

      .post-info {
        display: flex;
        align-items: center;

        a {
          z-index: 2;
        }

        span {
          font-weight: 500;
          color: var(--main-dark-shade-3);

          &.bullet {
            background: var(--main-dark-shade-3);
            height: 4px;
            width: 4px;
            border-radius: 50%;
            margin: 0 var(--spacing-xs);
          }

          b {
            color: var(--main-dark-shade-0);
          }
        }

        .author-a {
          display: flex;
          align-items: center;

          img {
            width: 24px;
            height: 24px;
            border-radius: 50%;
            margin-right: var(--spacing-12);
            object-fit: cover;
            object-position: center;
          }

          b {
            color: var(--main-dark-shade-0);
          }
        }
      }
    }

    &.horizontal {
      margin: var(--spacing-24) 0 0;
      padding: 0;

      > .article-relative {
        display: flex;
        align-items: center;

        .article-image {
          min-width: 300px;
          height: 160px;
          width: 100%;

          img {
            width: 100%;
          }
        }

        .info {
          margin: 0 0 0 var(--spacing-30);

          p:not(.title) {
            color: var(--accent-shade-1);
            font-weight: 500;
          }

          .title {
            margin: var(--spacing-12) 0 var(--spacing-24);
          }

          .post-info {
            span {
              color: var(--main-light-shade-4);

              &.bullet {
                background: var(--main-light-shade-4);
              }

              b {
                color: var(--main-light);
              }
            }
          }
        }
      }

      &.lg {
        > .article-relative {
          width: auto;
          flex-direction: row-reverse;

          .article-image {
            max-width: 770px;
            height: 464px;
            margin: 0;
          }

          .info {
            padding-right: var(--spacing-55);
            margin: 0;

            p:not(.title) {
              color: var(--accent-shade-2);
            }

            .title {
              font-size: 24px;
              margin: var(--spacing-16) 0 var(--spacing-24);
            }

            .body-1 {
              font-weight: normal;
              font-size: 18px;
              line-height: 150%;
              color: var(--main-dark-shade-3);
              margin-bottom: var(--spacing-16);
              text-transform: inherit;
            }

            .post-info {
              span {
                color: var(--main-dark-shade-3);

                &.bullet {
                  background: var(--main-dark-shade-3);
                }

                b {
                  color: var(--main-dark-shade-0);
                }
              }
            }
          }
        }
      }

      &.sm {
        > .article-relative .info .post-info span b {
          color: var(--main-dark-shade-0);
        }
      }
    }

    &.vertical {
      margin: var(--spacing-24) 0 0;
      padding-top: var(--spacing-24);

      .article-image {
        height: 220px;
      }

      &:nth-child(-n+3) {
        margin: 0;
      }

      &.lg {
        .article-image {
          height: 420px;
        }

        .info {
          margin-top: var(--spacing-24);

          p:not(.title) {
            font-weight: 500;
            font-size: 16px;
            line-height: 100%;
            text-transform: capitalize;
            color: var(--accent-shade-2);
          }

          .title {
            font-size: 24px;
            margin: var(--spacing-16) 0 var(--spacing-8);
          }

          .body-1 {
            font-weight: normal;
            font-size: 18px;
            line-height: 150%;
            color: var(--main-dark-shade-3);
            margin-bottom: var(--spacing-16);
            text-transform: inherit;
          }
        }
      }
    }

    &:hover {
      .article-image {
        transform: scale(1.01);
        transition: all var(--duration) ease;
      }

      .title {
        text-decoration: underline;
      }
    }
  }

  // .list .article.horizontal.sm > .article-relative .info .post-info span b[data-v-326a75c9]
  &.ourpicks .article.horizontal.sm {
    > .article-relative .info .post-info span b {
      color: var(--main-light);
    }
  }
}

@include bp($bp-mobile) {
  .list {
    .article.vertical,
    .article.horizontal {
      padding-top: 0;

      &.lg,
      &.sm {
        .article-relative {
          .article-image {
            height: 0;
            padding-bottom: 57%;
          }

          .info {
            margin-top: 16px;

            .title {
              margin-top: 12px;
              margin-bottom: 24px;
            }

            .body-1 {
              margin-bottom: 12px;
            }
          }
        }
      }

      &.lg .article-relative {
        .article-image {
          max-width: 100%;
          min-width: 0;
        }

        .info {
          padding-right: 0;

          .title {
            font-size: 18px;
          }

          .body-1 {
            font-size: 16px;
          }
        }
      }

      .article-relative {
        display: block;

        .col {
          float: unset;
        }

        .info {
          margin-left: 0;
        }
      }
    }
  }
}

@include bp($bp-phone) {
  .list + .list {
    margin-top: 36px;
  }
}

@include bp($bp-tablet) {
  .list.sm {
    width: 100%;
    padding: 0 12px;
  }
}
</style>
